'use client';

import * as React from 'react';
import { motion } from 'motion/react';
import { cn } from '../../lib/utils';
import { Badge } from '../../lib/admin';

interface BadgeDisplayProps {
  badges: Badge[];
  className?: string;
}

const badgeIcons: Record<string, string> = {
  primeiro_passo: 'ri-flag-line',
  contribuidor: 'ri-star-line',
  expert: 'ri-award-line',
  mestre: 'ri-trophy-line',
  lendario: 'ri-crown-fill',
  lendário: 'ri-crown-fill', // Fallback para acentuação
  copiador: 'ri-file-copy-line',
  power_user: 'ri-flashlight-line',
  poweruser: 'ri-flashlight-line', // Fallback sem underscore
};

// Normalizar badge_type para garantir consistência
const normalizeBadgeType = (badgeType: string): string => {
  return badgeType.toLowerCase().trim().replace(/\s+/g, '_');
};

const badgeColors: Record<string, string> = {
  primeiro_passo: 'from-blue-400 to-blue-600',
  contribuidor: 'from-green-400 to-green-600',
  expert: 'from-purple-400 to-purple-600',
  mestre: 'from-orange-400 to-orange-600',
  lendario: 'from-amber-500 to-yellow-600',
  lendário: 'from-amber-500 to-yellow-600', // Fallback para acentuação
  copiador: 'from-indigo-400 to-indigo-600',
  power_user: 'from-pink-400 to-pink-600',
  poweruser: 'from-pink-400 to-pink-600', // Fallback sem underscore
};

export function BadgeDisplay({ badges, className }: BadgeDisplayProps) {
  if (badges.length === 0) {
    return (
      <div className={cn('text-center py-8', className)}>
        <i className="ri-award-line text-4xl text-gray-300 dark:text-gray-600 mb-2"></i>
        <p className="text-sm text-gray-500 dark:text-[#c780ff] font-montserrat">
          Nenhum badge conquistado ainda
        </p>
      </div>
    );
  }

  return (
    <div className={cn('grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4', className)}>
      {badges.map((badge) => {
        // Normalizar badge_type para garantir consistência
        const normalizedType = normalizeBadgeType(badge.badge_type);
        const originalTypeLower = badge.badge_type.toLowerCase().trim();
        
        // Verificar se é lendario ANTES de buscar o ícone (mais robusto)
        const isLendario = normalizedType === 'lendario' || 
                          normalizedType === 'lendário' || 
                          originalTypeLower === 'lendario' || 
                          originalTypeLower === 'lendário' ||
                          originalTypeLower.includes('lendario') ||
                          originalTypeLower.includes('lendário');
        
        // Se for lendario, forçar o ícone diretamente
        let icon: string;
        if (isLendario) {
          icon = 'ri-crown-fill';
        } else {
          // Buscar ícone com fallback - tentar múltiplas variações
          icon = badgeIcons[normalizedType] || 
                 badgeIcons[badge.badge_type] || 
                 badgeIcons[badge.badge_type.toLowerCase()] ||
                 badgeIcons[badge.badge_type.toUpperCase()] ||
                 'ri-award-line';
        }
        
        // Log para debug se não encontrar o ícone esperado (exceto para lendario que já foi forçado)
        if (!isLendario && !badgeIcons[normalizedType] && !badgeIcons[badge.badge_type]) {
          console.warn(`Ícone não encontrado para badge_type: "${badge.badge_type}" (normalizado: "${normalizedType}"). Badge: ${badge.badge_name}. Usando fallback: ${icon}`);
        }
        
        // Buscar cores com fallback - tentar múltiplas variações
        let colors: string;
        if (isLendario) {
          colors = 'from-amber-500 to-yellow-600';
        } else {
          colors = badgeColors[normalizedType] || 
                   badgeColors[badge.badge_type] || 
                   badgeColors[badge.badge_type.toLowerCase()] ||
                   'from-gray-400 to-gray-600';
        }
        
        return (
          <motion.div
            key={badge.id}
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.2 }}
            className="group relative"
          >
            <div
              className={cn(
                'relative rounded-xl border border-gray-200 dark:border-[#8a2be2]/30 p-4',
                'bg-white dark:bg-gradient-to-br dark:from-[#1a0f2b] dark:to-[#2d1b3d]',
                'hover:shadow-lg transition-all duration-200 cursor-pointer',
                'hover:scale-105'
              )}
              title={badge.badge_description || badge.badge_name}
            >
              {/* Ícone do badge */}
              <div className={cn(
                'w-16 h-16 mx-auto mb-3 rounded-full bg-gradient-to-br',
                colors,
                'flex items-center justify-center text-2xl shadow-lg',
                isLendario
                  ? 'text-yellow-900' // Ícone escuro para contraste no fundo dourado
                  : 'text-white' // Ícone branco para os outros
              )}>
                {/* Renderizar ícone - garantir que sempre seja renderizado */}
                {isLendario ? (
                  <svg 
                    viewBox="0 0 24 24" 
                    fill="currentColor"
                    aria-hidden="true"
                    className="w-8 h-8"
                    style={{ display: 'block', width: '2rem', height: '2rem' }}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm2.7-1h8.6l.9-6.4-2.1 2.1L12 6l-3.1 4.7-2.1-2.1L7.7 15z"/>
                  </svg>
                ) : (
                  <i className={icon} aria-hidden="true" style={{ display: 'inline-block' }}></i>
                )}
              </div>

              {/* Nome do badge */}
              <h3 className="text-sm font-semibold text-center text-gray-900 dark:text-[#e0e1dd] font-ubuntu mb-1">
                {badge.badge_name}
              </h3>

              {/* Descrição (tooltip) */}
              {badge.badge_description && (
                <p className="text-xs text-center text-gray-500 dark:text-[#c780ff] font-montserrat line-clamp-2">
                  {badge.badge_description}
                </p>
              )}

              {/* Data de conquista */}
              <p className="text-xs text-center text-gray-400 dark:text-[#c780ff] mt-2 font-montserrat">
                {new Date(badge.earned_at).toLocaleDateString('pt-BR')}
              </p>
            </div>
          </motion.div>
        );
      })}
    </div>
  );
}
